import { Link } from '@tanstack/react-router';

import {
  FILE_EVENT,
  filePreviewLink,
  useWorkspaceChangeFeed,
} from '@/features/workspace-files';
import {
  STAGGER_CLASS,
  staggerStyle,
  useInitialIds,
} from '@/shared/motion/arrival';
import { formatElapsedSince, formatExactTime } from '@/shared/utils/time';
import { fileChangeLink } from '../links';
import {
  PANEL,
  ROW_INTERACTIVE_STATE,
} from '@/features/workspace-overview/stateStyles';
import { SECTION_HEADING } from '@/shared/components/sectionHeading';
import { PendingRows } from '@/shared/components/PendingRows';
import { changeSentence, summariseChanges } from '../changeSummary';

/** `ROW`'s shape, widened to the three columns this row needs. */
const CHANGE_ROW = `border-border relative grid grid-cols-[auto_minmax(0,1fr)_auto] items-baseline gap-x-2 border-t px-3.5 py-2 transition-colors first:border-t-0 motion-safe:duration-[var(--motion-duration-base)] ${ROW_INTERACTIVE_STATE}`;

/** How many events are read, and how many files are listed from them. */
const FEED_WINDOW = 25;
const ROWS = 5;

export interface RecentChangesSectionProps {
  workspaceId: string;
}

/**
 * What has changed in this workspace's files lately, and who changed it.
 *
 * This widget was specified, then cut during planning, and the reason it was
 * cut is worth keeping: the file-history endpoint required a `path`, so a
 * workspace feed would have meant one request per directory and still could
 * not have said who made a change — the directory listing carries a
 * modification time and no actor. That reasoning was correct, and it was about
 * the endpoints as they stood.
 *
 * It is a single read now. `/files/history` takes `path` as an optional filter
 * rather than a required argument, and every event in the log already carried
 * its workspace, its actor and its kind. The widget is here because the
 * contract changed, not because the objection was overruled.
 *
 * It is the page's one second read. Everything else on Overview comes from the
 * single workspace aggregate the board shares, and that stays true — this
 * query is separate, cached separately, and its failure cannot blank the rest
 * of the page.
 */
export function RecentChangesSection({
  workspaceId,
}: RecentChangesSectionProps) {
  // Enough events to fill five rows once they are grouped by file — two
  // tasks writing one shared file used to fill all five with it.
  const feed = useWorkspaceChangeFeed(workspaceId, FEED_WINDOW);
  const files = summariseChanges(feed.events, ROWS);
  // The rows that were here at first paint land a beat apart (spec 122
  // FR-024). Read before the early returns below: it is a hook.
  const initial = useInitialIds(files.map((file) => file.latest));

  // Its place is held while the first read is in flight (spec 126 FR-012):
  // rendering nothing and then five rows pushed Totals and Boards down the
  // page after first paint. The rows inside appear only if the read is slow.
  if (feed.isPending) {
    return (
      <section
        aria-labelledby="overview-changes"
        data-testid="recent-changes-reserved"
        className="flex min-h-72 flex-col gap-2.5"
      >
        <h2 id="overview-changes" className={SECTION_HEADING}>
          Agents produced
        </h2>
        <PendingRows label="Loading what agents produced" rows={5} />
      </section>
    );
  }

  // A read that failed is not a workspace where nothing has happened, and
  // saying "no changes yet" for an unreachable service would be a lie the
  // reader has no way to catch. Silence is the honest failure here: this is a
  // secondary widget, and the page's own answer does not depend on it.
  if (feed.loadFailed) return null;

  if (feed.events.length === 0) return null;

  return (
    <section
      aria-labelledby="overview-changes"
      className="flex flex-col gap-2.5"
    >
      <div className="flex items-baseline gap-3">
        <h2 id="overview-changes" className={SECTION_HEADING}>
          Agents produced
        </h2>
        {/*
          The count is the whole log, not the six rows shown — the same
          honesty the attention groups keep about what they left out.
        */}
        <Link
          to="/workspaces/$workspaceId/files"
          params={{ workspaceId }}
          className="text-muted-foreground hover:text-foreground focus-visible:ring-ring touch-pad ms-auto rounded-sm text-xs underline underline-offset-4 transition-colors focus-visible:ring-3 focus-visible:outline-none motion-safe:duration-[var(--motion-duration-base)]"
        >
          {feed.total > feed.events.length
            ? `All ${String(feed.total)} in Files`
            : 'Open Files'}
        </Link>
      </div>

      <ul className={PANEL}>
        {files.map((file, index) => {
          const event = file.latest;
          const { icon: Icon } = FILE_EVENT[event.event_type];
          const sentence = changeSentence(file);
          const name = event.path.split('/').pop() ?? event.path;
          // Two different files can share a name (a README at the root and
          // one in a task's folder); then the folder says which is which.
          const folder = event.path.includes('/')
            ? event.path.slice(0, event.path.lastIndexOf('/'))
            : '';
          const ambiguous =
            files.filter((other) => other.path.split('/').pop() === name)
              .length > 1;

          return (
            <li
              key={event.id}
              // The first rows land a beat apart on first paint (spec 122
              // FR-024); a row that arrives later lands at once.
              className={`${CHANGE_ROW} ${initial.has(event.id) ? STAGGER_CLASS : ''}`}
              style={initial.has(event.id) ? staggerStyle(index) : undefined}
            >
              <Icon
                aria-hidden="true"
                className="text-muted-foreground size-3.5 shrink-0 translate-y-0.5 self-start"
                strokeWidth={1.75}
              />
              {/*
                One sentence, actor first (spec 153 FR-015): "Payments produced
                test_cases.md". The file's name, not its path: the folder is on
                its title, and named only when two files share a name. Two lines on
                a phone (spec 265), so the name is not the part that is cut.
              */}
              <p className="text-muted-foreground min-w-0 truncate text-xs max-md:line-clamp-2 max-md:whitespace-normal max-md:[overflow-wrap:anywhere]">
                <span className="text-foreground">{sentence.who}</span>{' '}
                {sentence.verb}{' '}
                <Link
                  {...fileChangeLink(workspaceId, event, (path) =>
                    filePreviewLink(workspaceId, path),
                  )}
                  title={event.path}
                  className="text-foreground font-medium underline-offset-4 after:absolute after:inset-0 hover:underline focus-visible:outline-none"
                >
                  {name}
                </Link>
                {ambiguous ? ` in ${folder || 'the workspace root'}` : null}
                {file.count > 1 ? ` · ${String(file.count)} changes` : null}
              </p>
              <span
                title={formatExactTime(event.occurred_at)}
                className="text-muted-foreground shrink-0 self-start text-xs tabular-nums"
              >
                {formatElapsedSince(event.occurred_at)}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
